import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RichContent } from '@/components/content/RichContent'
import { useIsBelowMd } from '@/hooks/useLayoutMode'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { GetContentBundleResponse } from '@/types/api'

interface ContentNavigatorItem {
  id: number
  displayName: string
  content: string | null
  isDraft?: boolean
  originalFields?: Record<string, string | null>
}

interface ContentNavigatorProps {
  items: ContentNavigatorItem[]
  bundle: GetContentBundleResponse
  emptyMessage?: string
  navLabel?: string
  onItemSelect?: (item: ContentNavigatorItem) => void
}

export function ContentNavigator({ items, bundle, emptyMessage, navLabel, onItemSelect }: ContentNavigatorProps) {
  const { t } = useTranslation()
  const resolvedEmptyMessage = emptyMessage ?? t('contentNavigator.emptyDefault')
  const resolvedNavLabel = navLabel ?? t('contentNavigator.navLabelDefault')
  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)
  const [navCollapsed, setNavCollapsed] = useState(false)
  // Below md the nav is an expandable list stacked above the content instead of a
  // side column: item names are full sentences and do not survive a narrow column
  // or a single-line horizontal scroller. The side-column collapse toggle is hidden
  // there, so a `navCollapsed` left over from a wider viewport must not hide
  // every item.
  const isBelowMd = useIsBelowMd()
  const collapsed = navCollapsed && !isBelowMd
  const [listOpen, setListOpen] = useState(false)

  // Ref so the mount effect always calls the latest callback without being a dependency
  const onItemSelectRef = useRef(onItemSelect)
  onItemSelectRef.current = onItemSelect
  // Capture initial item at first render so the effect doesn't depend on `items`
  const initialItemRef = useRef(items[0])

  useEffect(() => {
    if (initialItemRef.current) onItemSelectRef.current?.(initialItemRef.current)
  }, [])

  if (items.length === 0) {
    return <p className="empty-state">{resolvedEmptyMessage}</p>
  }

  const selectedItem = selectedItemId
    ? items.find((m) => m.id === selectedItemId)
    : items[0] ?? null

  const handleItemClick = (item: ContentNavigatorItem) => {
    setSelectedItemId(item.id)
    onItemSelect?.(item)
  }

  const draftBadge = (item: ContentNavigatorItem) => item.isDraft && (
    <span className="tree-draft-badge" title={t('draftPreview.itemBadgeAdditionalReading')} aria-label={t('draftPreview.itemBadgeAdditionalReading')}>
      {t('sidebar.draftBadgeLabel')}
    </span>
  )

  const navItemButton = (item: ContentNavigatorItem, onSelect: (item: ContentNavigatorItem) => void) => (
    <button
      key={item.id}
      className={`matter-nav-item${selectedItem?.id === item.id ? ' active' : ''}`}
      aria-current={selectedItem?.id === item.id ? 'true' : undefined}
      onClick={() => onSelect(item)}
    >
      {item.displayName}
      {draftBadge(item)}
    </button>
  )

  return (
    <div className={`matter-layout${collapsed ? ' matter-layout--collapsed' : ''}`}>
      {isBelowMd ? (
        <nav className="matter-nav matter-nav--list" aria-label={resolvedNavLabel}>
          <button
            className="matter-nav-summary"
            onClick={() => setListOpen((o) => !o)}
            aria-expanded={listOpen}
            aria-controls="matter-nav-items"
          >
            <span className="matter-nav-summary-label">
              {selectedItem?.displayName ?? resolvedNavLabel}
              {selectedItem && draftBadge(selectedItem)}
            </span>
            <ExpandMoreIcon
              className={`matter-nav-summary-chevron${listOpen ? ' matter-nav-summary-chevron--open' : ''}`}
              fontSize="small"
            />
          </button>

          {listOpen && (
            <div id="matter-nav-items" className="matter-nav-items">
              {items.map((item) => navItemButton(item, (i) => { handleItemClick(i); setListOpen(false) }))}
            </div>
          )}
        </nav>
      ) : (
        <nav className="matter-nav" aria-label={resolvedNavLabel}>
          <button
            className="matter-nav-toggle"
            onClick={() => setNavCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('contentNavigator.expandNavigation') : t('contentNavigator.collapseNavigation')}
            title={collapsed ? t('contentNavigator.expandNavigation') : t('contentNavigator.collapseNavigation')}
          >
            <span className="matter-nav-toggle-icon">{collapsed ? '›' : '‹'}</span>
          </button>

          {!collapsed && items.map((item) => navItemButton(item, handleItemClick))}
        </nav>
      )}

      {selectedItem && (
        <div className="matter-content" id={`reading-${selectedItem.id}`}>
          <h2 className="section-heading">{selectedItem.displayName}</h2>
          <RichContent html={selectedItem.content ?? ''} original={selectedItem.originalFields?.content} bundle={bundle} />
        </div>
      )}
    </div>
  )
}
